import { useRef, useState, type JSX, type ReactNode } from 'react';

interface Props {
  /** Texte affiché ET contenu de l'infobulle (sauf si `tooltip` est fourni). */
  text: string;
  /** Infobulle explicite quand elle apporte plus que le texte affiché (une adresse e-mail derrière un nom, par exemple). */
  tooltip?: string | null;
  className?: string;
  /** Contenu réellement rendu, si différent du texte brut (mise en évidence, icône…). */
  children?: ReactNode;
}

/**
 * Texte de cellule qui n'affiche une infobulle QUE s'il est réellement coupé.
 *
 * POURQUOI MESURER PLUTÔT QUE TOUJOURS POSER UN `title`. Un `title` permanent
 * fait surgir une infobulle sur chaque cellule survolée, y compris quand elle
 * répète mot pour mot ce qui est déjà lisible : dans un tableau, c'est du bruit
 * continu. On compare donc `scrollWidth` à `clientWidth` — le seul test fiable
 * de « ce texte est tronqué », l'ellipse étant purement visuelle et invisible du
 * DOM.
 *
 * LA MESURE A LIEU AU SURVOL, pas au rendu ni via un ResizeObserver : c'est le
 * seul moment où la réponse est utile, elle tient compte de la largeur de colonne
 * du moment (elles sont redimensionnables), et cela évite un observateur par
 * cellule sur une liste qui en compte des centaines.
 */
export function CellText({ text, tooltip, className, children }: Props): JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    // +1 px de tolérance : les largeurs sub-pixel rendent l'égalité stricte
    // faussement « tronquée » sur certains facteurs de zoom.
    const clipped = el.scrollWidth > el.clientWidth + 1;
    const full = (tooltip ?? text).trim();
    const next = clipped && full ? full : undefined;
    // Ne re-rend que si le verdict change.
    setTitle((cur) => (cur === next ? cur : next));
  };

  return (
    <span ref={ref} className={className} title={title} onMouseEnter={measure} onFocus={measure}>
      {children ?? text}
    </span>
  );
}
